import { Component, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { finalize, switchMap, tap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

import { marker as bitfToTranslate } from '@biesbjerg/ngx-translate-extract-marker';

import { Archive, ArchiveFile } from '@common/core/models';
import {
  UiRoleManagerService,
  ArchivesService,
  LoaderService,
  StoreService,
  FilesService,
  DialogsService,
} from '@services';
import { EBitfCloseEventStatus, ERoleActions } from '@common/enums';
import { IBitfApiResponse, IBitfCloseEvent, IBitfGraphQlResponse } from '@common/interfaces';
import { BitfFile } from '@common/libs/bitforce/core/models';

import { FileUploaderComponent } from '../file-uploader/file-uploader.component';
import { CONSTANTS } from '@web/constants';
import { bitfPromptToDownloadCorsImage } from '@common/libs/bitforce/utils/bitf-files.utils';

@Component({
  selector: 'mpa-archive-file-manager',
  templateUrl: './archive-file-manager.component.html',
  styleUrls: ['./archive-file-manager.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ArchiveFileManagerComponent implements OnInit {
  @ViewChild(FileUploaderComponent) fileUploader: FileUploaderComponent;
  get selectedItem(): Archive {
    return this.storeService.store.selectedItem;
  }

  eRoleActions = ERoleActions;

  get filesList(): ArchiveFile[] {
    return this.selectedItem?.files || [];
  }

  constructor(
    public uiRoleManagerService: UiRoleManagerService,
    private loaderService: LoaderService,
    private translateService: TranslateService,
    private archiveService: ArchivesService,
    private filesService: FilesService,
    private dialogsService: DialogsService,
    public storeService: StoreService
  ) {}

  ngOnInit(): void {}

  trackByFileId(index: number, file: ArchiveFile): number | string {
    return file.id ?? index;
  }

  getFileTypeIcon(file: ArchiveFile): string {
    const extension = file.fileExtension?.toLowerCase();
    switch (extension) {
      case 'pdf':
        return 'picture_as_pdf';
      case 'xls':
      case 'xlsx':
      case 'csv':
        return 'table_chart';
      case 'ppt':
      case 'pptx':
        return 'slideshow';
      case 'zip':
      case 'rar':
      case '7z':
        return 'archive';
      case 'mp4':
      case 'mov':
      case 'avi':
      case 'mkv':
      case 'webm':
        return 'movie';
      default:
        return 'description';
    }
  }

  onDownloadFile(file: ArchiveFile) {
    bitfPromptToDownloadCorsImage({ fileUrl: file.url, fileName: file.name });
  }

  onRemoveFile(file: ArchiveFile) {
    const dialog = this.dialogsService.dialog.open(CONSTANTS.okCancelDialogComponent, {
      ...CONSTANTS.SMALL_DIALOG_SIZE,
      data: {
        title: this.translateService.instant('ITEM.DETAIL.DELETE_FILE'),
        message: this.translateService.instant('ITEM.DETAIL.CONFIRM_REMOVING_FILE'),
        cancelText: bitfToTranslate('BITF.LABEL.CANCEL'),
        okText: bitfToTranslate('BITF.LABEL.OK'),
      },
    });
    dialog.afterClosed().subscribe((result: IBitfCloseEvent<void>) => {
      if (result?.status === EBitfCloseEventStatus.OK) {
        this.loaderService.show();
        this.filesService
          .delete({ id: file.id })
          .pipe(
            switchMap(_ => this.archiveService.getItemById({ id: this.storeService.store.selectedItem.id }))
          )
          .subscribe((response: IBitfApiResponse<Archive>) => {
            this.storeService.updateSelectedItem({ files: response.content.files });
          });
      }
    });
  }

  onUpload() {
    const files: BitfFile[] = Array.from(this.fileUploader.uploadableFiles);

    const observableList = files.map(file =>
      this.filesService.createFile({
        variables: { file },
        context: { useMultipart: true },
        disableHideLoader: true,
      })
    );

    if (observableList.length > 0) {
      this.loaderService.show();
      this.filesService
        .getThrottledObservables(observableList, 1)
        .pipe(
          switchMap((responses: IBitfGraphQlResponse<ArchiveFile>[]) => {
            const updateArchive = new Archive(undefined, {
              id: this.selectedItem.id,
              files: this.selectedItem.files,
            });
            updateArchive.files.push(...responses.map(response => response.content));
            return this.archiveService.updateItem({
              disableHideLoader: true,
              body: updateArchive,
            });
          }),
          tap((response: IBitfGraphQlResponse<Archive>) => {
            this.storeService.updateSelectedItem(response.content);
          }),
          finalize(() => {
            this.loaderService.hide();
            this.fileUploader.removeUploadedFiles();
          })
        )
        .subscribe();
    }
  }
}
